import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { html } from 'lit';
import { hasPermission } from '../../../db/utils/util-permissions.js';
import { NoPermission } from '../../../permissions/client/NoPermission.js';
import { Permission, Role } from '../../../permissions/roles.js';
import { Person } from '../../../users/entities/person.js';
import { JobTicketEditor } from './JobTicketEditor.js';
import { JOB_TICKET_PERSON_SELECTION } from './jobTicketPerson.js';

/**
 * Deutschland-/Job-Ticket of an employee. Loads the person, resolves who may see and edit the
 * ticket and hands the rest over to {@link JobTicketEditor}.
 */
export const MitarbeiterTicket = stateful((props: { personID: Maybe<string> }) => {
  const personID = props.personID;
  if (!personID) throw new Error('No user given');

  const user = useObservable(() => currentUser<Person>(), []);
  const {
    value: person,
    loading,
    error,
  } = useQuery(() => subscribeByID(Person, personID)(JOB_TICKET_PERSON_SELECTION), [personID]);

  if (error)
    return html`
      <div class="card-container">
        <div class="alert alert-error">
          <span class="icon-[material-symbols--error-outline] text-lg"></span>
          Die Ticketdaten konnten nicht geladen werden: ${error.message}
        </div>
      </div>
    `;

  if (loading || !user) return PageLoader({ cardOptions: [{ title: 'Job-Ticket' }] });

  if (!person)
    return NoPermission({
      title: 'Keine Daten gefunden',
      message: 'Zu dieser Person konnten keine Daten geladen werden.',
    });

  const isSelf = user._id === person._id;
  /** employees always administrate their own ticket, everyone else needs the ticket permission */
  const hasEditPermission =
    isSelf && person.roles.some(role => role.name === Role.MITARBEITER)
      ? true
      : hasPermission(user, person, Permission.TICKET_INVITE);

  if (!isSelf && !hasEditPermission)
    return NoPermission({ message: 'Sie haben nicht die nötige Berechtigung, um diese Seite zu sehen!' });

  return JobTicketEditor({
    person,
    hasEditPermission,
    canEditFields: isSelf || hasEditPermission,
    canSaveWithoutValidation: Boolean(user.hasPermission(Permission.TICKET_INVITE, GLOBAL_CONTEXT)),
  });
});
